import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Modal, message, Space, Tag, Input, Checkbox } from 'antd';
import { SearchOutlined, EyeOutlined, ReloadOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { ResponsePageMbcAgentData, MbcAgentData, MbcLead } from '../types/auth';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';

const { Search } = Input;

const MBC: React.FC = () => {
  const [agentsData, setAgentsData] = useState<ResponsePageMbcAgentData>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyWithOpportunities, setOnlyWithOpportunities] = useState(false);
  const [isOpportunitiesModalVisible, setIsOpportunitiesModalVisible] = useState(false);
  const [selectedOpportunities, setSelectedOpportunities] = useState<MbcLead[]>([]);
  const [selectedAgentName, setSelectedAgentName] = useState<string>('');
  const navigate = useNavigate();

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getMBCAgents(
        currentPage,
        pageSize,
        searchQuery || undefined,
        onlyWithOpportunities || undefined
      );
      setAgentsData(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view MBC agents.');
        setAgentsData({
          content: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 10
        });
      } else {
        message.error('Failed to load agents');
        setAgentsData({
          content: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 10
        });
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, onlyWithOpportunities, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    
    loadAgents();
  }, [navigate, loadAgents]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, onlyWithOpportunities]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleViewOpportunities = (leads: MbcLead[], agentName: string) => {
    setSelectedOpportunities(leads);
    setSelectedAgentName(agentName);
    setIsOpportunitiesModalVisible(true);
  };

  const handleOpportunitiesModalClose = () => {
    setIsOpportunitiesModalVisible(false);
    setSelectedOpportunities([]);
    setSelectedAgentName('');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await apiService.refreshMBC();
      message.success('Refreshed successfully');
      // Reload agents after refresh
      await loadAgents();
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to refresh MBC.');
      } else {
        message.error('Failed to refresh');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success('URL copied to clipboard');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('URL copied to clipboard');
      } catch (err) {
        message.error('Failed to copy URL');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadOpportunitiesCSV = (leads: MbcLead[], agentName: string) => {
    if (leads.length === 0) {
      message.warning('No opportunities to download');
      return;
    }

    // Create CSV content
    const headers = ['Opportunity ID', 'Status', 'URL'];
    const rows = leads.map(lead => [lead.id, lead.status, lead.url || '']);
    
    // Escape CSV values
    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    // Add BOM for UTF-8 support in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = `opportunities_${agentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`;
    link.download = fileName;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(url);
    message.success(`Opportunities exported to ${fileName}`);
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: ['agent', 'name'],
      key: 'name',
      render: (name: string, record: MbcAgentData) => name || '-',
    },
    {
      title: 'Email',
      dataIndex: ['agent', 'email'],
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'Phone',
      dataIndex: ['agent', 'phone'],
      key: 'phone',
    },
    {
      title: 'Created At',
      dataIndex: ['agent', 'createdAt'],
      key: 'createdAt',
      render: (createdAt: string) => formatDateTime(createdAt),
    },
    {
      title: 'Opportunities Count',
      key: 'opportunitiesCount',
      render: (_: any, record: MbcAgentData) => (
        <Tag color="blue">{record.leads.length}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: MbcAgentData) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewOpportunities(record.leads, record.agent.name || record.agent.phone)}
          >
            View Opportunities
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>MBC Agents</h2>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Checkbox
            checked={onlyWithOpportunities}
            onChange={(e) => setOnlyWithOpportunities(e.target.checked)}
            style={{ fontSize: '14px' }}
          >
            Only with opportunities
          </Checkbox>
          <Search
            placeholder="Search agents by name, email, or phone"
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: 400 }}
            onSearch={handleSearch}
          />
          <Button
            type="default"
            icon={<ReloadOutlined />}
            size="large"
            onClick={handleRefresh}
            loading={refreshing}
          >
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>
      </div>

      {(searchQuery || onlyWithOpportunities) && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontWeight: 500 }}>Active filters:</span>
          {searchQuery && (
            <Tag closable onClose={() => setSearchQuery('')}>
              Search: {searchQuery}
            </Tag>
          )}
          {onlyWithOpportunities && (
            <Tag closable onClose={() => setOnlyWithOpportunities(false)}>
              Only with opportunities
            </Tag>
          )}
        </div>
      )}

      <Table
        columns={columns}
        dataSource={agentsData.content}
        loading={loading}
        rowKey={(record) => record.agent.phone}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: agentsData.totalElements,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} agents`,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
      />

      <Modal
        title={`Opportunities for ${selectedAgentName}`}
        open={isOpportunitiesModalVisible}
        onCancel={handleOpportunitiesModalClose}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadOpportunitiesCSV(selectedOpportunities, selectedAgentName)}
            disabled={selectedOpportunities.length === 0}
          >
            Download CSV
          </Button>,
          <Button key="close" onClick={handleOpportunitiesModalClose}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedOpportunities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            No opportunities found for this agent
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Total opportunities: {selectedOpportunities.length}</strong>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {selectedOpportunities.map((lead, index) => (
                <div
                  key={lead.id || index}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <strong>Opportunity ID:</strong> <span style={{ fontFamily: 'monospace' }}>{lead.id}</span>
                    </div>
                    <Tag color={lead.status === 'active' ? 'green' : lead.status === 'closed' ? 'red' : 'blue'}>
                      {lead.status}
                    </Tag>
                  </div>
                  {lead.url && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <a 
                        href={lead.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ color: '#1890ff' }}
                      >
                        View in SalesForce
                      </a>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.preventDefault();
                          handleCopyUrl(lead.url);
                        }}
                        style={{ padding: '0 4px', height: 'auto' }}
                        title="Copy URL"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MBC;
